// Locale-aware number formatting for the facts the terroir layer shows.
// Intl handles the decimal separator (1,5 km in Dutch, 1.5 km in English);
// callers pass the unit so the digits and the unit stay one string.
import type { Locale } from "./i18n";

export function formatNumber(locale: Locale, value: number, maxFractionDigits = 0): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: maxFractionDigits }).format(value);
}

export function formatKm(locale: Locale, km: number): string {
  return `${formatNumber(locale, km, km < 10 ? 1 : 0)} km`;
}

export function isNum(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** A price in the offer's own currency, from integer cents. */
export function formatMoney(locale: Locale, cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(cents / 100);
  } catch {
    // An unknown currency code throws a RangeError; the digits are still right.
    return `${formatNumber(locale, cents / 100, 2)} ${currency}`;
  }
}

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;

/** The eight-point compass name of a bearing in degrees. */
export function compassOf(degrees: number): string {
  const index = Math.round((((degrees % 360) + 360) % 360) / 45) % COMPASS.length;
  return COMPASS[index] ?? "N";
}

/** An http(s) URL safe to put in an href, or undefined: anything scraped from
 * a third party is checked so a `javascript:` value can never become a link. */
export function externalHref(raw: string | undefined): string | undefined {
  if (raw === undefined || raw === "") return undefined;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}
